import type { ReactNode } from 'react';
import { MarketingShell } from '../components/MarketingShell';

const LAST_UPDATED = '12 May 2026';

// Terms of service for Answer Assist and My Dyslexia Tools.
// Plain English. Designed to be read once and never argued with.
// Reviewed by a real solicitor before App Store submission.
export function TermsScreen() {
  return (
    <MarketingShell>
      <article className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-semibold tracking-tight">Terms of Service</h1>
        <p className="text-ink/50 text-sm mt-1">Last updated: {LAST_UPDATED}</p>

        <Section title="Who we are and what this is">
          <p>
            Answer Assist and My Dyslexia Tools are products of Creative Sauce Ltd ("we",
            "us", "our"). The website at mydyslexiatools.com, the Answer Assist app, any apps
            in the My Dyslexia Tools portfolio, and any related services we offer (together,
            the "Service") are provided to you on the terms below.
          </p>
          <p>
            By using the Service you agree to these terms. If you do not agree, please do not
            use the Service.
          </p>
        </Section>

        <Section title="Who can use the Service">
          <p>
            The Service is intended to be used by children with dyslexia, with adult support if
            needed, and by adults with dyslexia. We design and operate the Service to be safe
            for people of all ages, but the legal contract here is with an adult: a parent,
            guardian, teacher, or the adult user themselves.
          </p>
          <p>
            If you are a parent or guardian, you are responsible for your child's use of the
            Service and the agreement to these terms.
          </p>
        </Section>

        <Section title="What you can use the Service for">
          <p>You may use the Service to:</p>
          <ul>
            <li>Photograph paper worksheets you have been given and need to complete.</li>
            <li>Dictate, type, and place answers on those worksheets.</li>
            <li>Export, save, print, and share the finished worksheets.</li>
          </ul>
          <p>You may not use the Service to:</p>
          <ul>
            <li>
              Process photographs of people, identifying documents, medical records, financial
              records, or other materials that are not the kind of worksheet the Service is
              designed for.
            </li>
            <li>Process content that is illegal, harassing, or abusive.</li>
            <li>Attempt to break, abuse, or bypass the safeguarding features of the Service.</li>
            <li>
              Attempt to reverse engineer, decompile, or extract our source code, AI prompts, or
              any underlying intellectual property.
            </li>
            <li>Use the Service to compete with us, including by training a model on it.</li>
          </ul>
        </Section>

        <Section title="Account and accounts (or lack thereof)">
          <p>
            The Service does not require an account. You do not give us a name or email to use
            it. There is therefore nothing to log into and nothing to reset. If we ever change
            that, we will tell you here first.
          </p>
        </Section>

        <Section title="In-app purchases">
          <p>
            Some parts of the Service may be unlocked by a one-off in-app purchase made through
            the Apple App Store or Google Play. These purchases are processed by Apple or Google
            and you are also bound by their store terms.
          </p>
          <p>
            We will not auto-charge you, will not auto-renew, and will not offer subscriptions
            unless this section is updated to say otherwise.
          </p>
          <p>
            Refund requests go through Apple or Google. We do not directly take payments and
            cannot directly issue refunds.
          </p>
        </Section>

        <Section title="Safeguarding">
          <p>
            We take safeguarding seriously. The Service is designed to refuse photographs that
            do not look like worksheets. If a child dictates language that suggests they are
            in distress, the Service stops the worksheet flow and provides links to UK support
            services including the Samaritans, Childline, and the NSPCC.
          </p>
          <p>
            Nothing in the Service is medical or mental health advice. If you or your child are
            in crisis, contact a real human service: in the United Kingdom, Samaritans on 116
            123, Childline on 0800 1111, or 999 in an emergency.
          </p>
        </Section>

        <Section title="Intellectual property">
          <p>
            We own the Service, including the design, code, prompts, marketing copy, and brand.
            You may not copy, redistribute, or build derivative products from any of it without
            our written permission.
          </p>
          <p>
            You own the worksheets you photograph and the answers you write. Using the Service
            does not give us any ownership of that content. We do not claim a licence in the
            sense of being allowed to use, display, or train on your content beyond what is
            necessary to provide the immediate result you asked for (rendering a finished
            worksheet you can export).
          </p>
        </Section>

        <Section title="Disclaimers">
          <p>
            The Service is provided "as is" and "as available". The Service may sometimes get
            answers in the wrong place, miss a question, mis-hear a dictated word, or fail to
            recognise a worksheet. We do everything we sensibly can to make it accurate, but no
            software is perfect. Always proofread the finished worksheet before sending it back
            to school.
          </p>
          <p>
            We do not guarantee that the Service will always be available, that it will be
            error-free, or that it will be compatible with every device. We do not guarantee
            that AI mode produces the correct answer.
          </p>
          <p>
            To the extent the law allows, we exclude all implied warranties.
          </p>
        </Section>

        <Section title="Limit of our liability">
          <p>
            Nothing in these terms excludes our liability for death, personal injury caused by
            our negligence, fraud, or any other liability that the law does not allow us to
            limit.
          </p>
          <p>
            Subject to that, our total liability to you for any claim relating to the Service
            is limited to the greater of: (a) the amount you have paid us for the Service in
            the twelve months before the claim arose; or (b) twenty pounds sterling.
          </p>
        </Section>

        <Section title="Ending the agreement">
          <p>
            You can stop using the Service at any time by deleting the app from your device. We
            may suspend or end your access to the Service if you breach these terms or abuse
            the Service. We will let you know when we can, except where doing so would itself
            cause harm.
          </p>
        </Section>

        <Section title="Changes to these terms">
          <p>
            We may update these terms from time to time. The "Last updated" date at the top
            tells you when. If we make a material change we will say so in the app the next
            time you open it. Continuing to use the Service after a change means you agree to
            the updated terms.
          </p>
        </Section>

        <Section title="Governing law">
          <p>
            These terms are governed by the law of England and Wales. Any dispute is decided
            in the courts of England and Wales. If you live in a different country, this does
            not affect any consumer rights you have that cannot be contracted out of in your
            local law.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions about these terms:{' '}
            <a className="text-accent hover:underline" href="mailto:support@mydyslexiatools.com">
              support@mydyslexiatools.com
            </a>
            .
          </p>
        </Section>
      </article>
    </MarketingShell>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
      <div className="mt-3 leading-relaxed text-ink/80 space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1">
        {children}
      </div>
    </section>
  );
}
